import { useMemo } from "react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { MateriaisECoresDoServico } from "./MateriaisECoresDoServico";
import { orderedSystems, systemLabel, type Shade } from "./shade-groups";

type Props = {
  materialOptionIds: string[];
  onMaterialOptionIds: (ids: string[]) => void;
  /** Material → escalas de cor. Só os materiais tocados entram; o resto o servidor não mexe. */
  escalasPorMaterial: Record<string, string[]>;
  onEscalasPorMaterial: (v: Record<string, string[]>) => void;
};

/**
 * Materiais e cores do serviço no Catálogo: o cardápio e as escalas do laboratório, e "Novo
 * material" grava na hora. A tela em si é `MateriaisECoresDoServico` (a mesma do onboarding).
 */
export function ServicoMateriaisECores({ materialOptionIds, onMaterialOptionIds, escalasPorMaterial, onEscalasPorMaterial }: Props) {
  const utils = trpc.useUtils();
  const ofertasQuery = trpc.prosthesis.catalog.materialOptions.useQuery();
  const coresQuery = trpc.prosthesis.catalog.myShadeOptions.useQuery();

  const ofertas = useMemo(
    () => [...(ofertasQuery.data ?? [])].sort((a, b) => a.label.localeCompare(b.label, "pt-BR")),
    [ofertasQuery.data],
  );
  const escalas = useMemo(
    () => orderedSystems((coresQuery.data ?? []) as Shade[]).map((codigo) => ({ codigo, rotulo: systemLabel(codigo) })),
    [coresQuery.data],
  );

  const salvarOferta = trpc.prosthesis.catalog.salvarMaterialOption.useMutation({
    onError: (e) => toast.error(e.message || "Erro ao adicionar material."),
  });

  return (
    <MateriaisECoresDoServico
      ofertas={ofertas}
      materialIds={materialOptionIds}
      onMaterialIds={onMaterialOptionIds}
      escalasPorMaterial={escalasPorMaterial}
      onEscalasPorMaterial={onEscalasPorMaterial}
      escalas={escalas}
      criandoMaterial={salvarOferta.isPending}
      onNovoMaterial={async (label) => {
        const { id } = await salvarOferta.mutateAsync({ label });
        await utils.prosthesis.catalog.materialOptions.invalidate();
        onMaterialOptionIds([...materialOptionIds, id]);
        toast.success("Material adicionado ao seu cardápio.");
      }}
      vazio={ofertasQuery.isLoading ? undefined : "Seu cardápio está vazio. Crie o primeiro material acima."}
    />
  );
}


/** As escalas já declaradas por material, lidas das cores do laboratório: uma escala conta como
 *  declarada quando qualquer cor dela aponta pro material. */
export function escalasJaDeclaradas(cores: Shade[] | undefined) {
  const mapa: Record<string, string[]> = {};
  for (const cor of cores ?? []) {
    for (const materialId of (cor as any).compatibleMaterialOptionIds ?? []) {
      const atuais = mapa[materialId] ?? [];
      if (!atuais.includes(cor.system)) mapa[materialId] = [...atuais, cor.system];
    }
  }
  return mapa;
}
